import { useState, useContext } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../../api/api";
import "./Auth.css";
import { AuthContext } from "../../context/AuthContext";
import { GoogleLogin } from "@react-oauth/google";

function Signup() {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const referredByCode = searchParams.get("ref");
  
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setError("");
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const sendTimezone = async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try { await api.post("/auth/set-timezone", { timezone }); } catch {}
  };

  // Signup.jsx — handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/signup", { ...form, referredByCode });
      await sendTimezone();
      // ✅ Pass email to login page so they don't retype it
      navigate("/login", { state: { pendingVerification: true, email: form.email } });
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      setLoading(true);
      const res = await api.post("/auth/google", { 
        credential: response.credential,
        referredByCode 
      });
      setUser(res.data.user);
      await sendTimezone();
      navigate("/tracks");
    } catch (err) {
      setError(err.response?.data?.message || "Google signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">

        {/* Logo */}
        <div className="auth-logo">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="auth-logo-svg">
            <rect width="28" height="28" rx="7" fill="#09090b"/>
            <rect x="7" y="16" width="14" height="2.5" rx="1.25" fill="white" opacity="0.4"/>
            <rect x="7" y="12" width="10" height="2.5" rx="1.25" fill="white" opacity="0.7"/>
            <rect x="7" y="8" width="6" height="2.5" rx="1.25" fill="white"/>
            <path d="M19 9.5L21.5 12L19 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="auth-logo-name">learnlia</span>
        </div>

        {/* Heading */}
        <h1 className="auth-heading">Create your account</h1>
        <p className="auth-subheading">Free forever. Start learning in minutes.</p>

        <div className="auth-google-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError("Google Login Failed")}
            useOneTap
            theme="filled_black"
            shape="pill"
            width="100%"
          />
        </div>

        <div className="auth-divider-container">
          <div className="auth-divider-line"></div>
          <span className="auth-divider-text">or signup with email</span>
          <div className="auth-divider-line"></div>
        </div>

        {/* Steps */}
        <div className="auth-steps">
          {["Create account", "Verify email", "Start learning"].map((label, i) => (
            <div key={i} className={`auth-step ${i === 0 ? "active" : ""}`}>
              <div className="auth-step-num">{i + 1}</div>
              <span className="auth-step-text">{label}</span>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label">Name</label>
            <input
              className="auth-input"
              type="text"
              name="name"
              placeholder="Your name"
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">Email</label>
            <input
              className="auth-input"
              type="email"
              name="email"
              placeholder="you@example.com"
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">Password</label>
            <input
              className="auth-input"
              type="password"
              name="password"
              placeholder="Min. 8 characters"
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : "Create account"}
          </button>
        </form>

        <div className="auth-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>

      </div>
    </div>
  );
}

export default Signup;